import { Component, ElementRef, AfterViewInit, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { CdkDragDrop, CdkDragMove, moveItemInArray } from '@angular/cdk/drag-drop';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';
import { NotificationService } from '../../service/notification.service';
import { NotificationType } from '../../enum/notification-type.enum';
import { ReviewCommissionsModel } from 'src/app/models/commission-summary/reviewcommissions.model';
import { ReviewCommissionsService } from 'src/app/service/commission-summary/review-commissions.service';
import { CommissionReportConfigService } from '../../service/commission-summary/commission-report-config.service';
import { GlobalService } from 'src/app/service/global.service';
import { ExportExcelService } from 'src/app/service/export-excel.service';
import { ManageCodesService } from 'src/app/service/managecodes.service';
import { AddUser } from 'src/app/models/manageUsers/add-user.model';
import {
  applyConfiguredTotals,
  applyKpiConfigOrderFromList,
  calculateKpiColumnTotals,
  buildAllOnKpiConfigs,
  buildCommissionReportExcelPayload,
  CHARGEBACK_DISPLAY_NOTICE,
  CHARGEBACK_DISPLAY_NOTICE_TITLE,
  CommReportDisplayState,
  CommReportEmployeeRow,
  CommReportKpiConfigByPlan,
  ensureKpiConfigValid,
  formatKpiNumber,
  formatKpiPercentage,
  getKpiCellDisplayLines,
  getKpiColumnSubtitleParts,
  getKpiCellDisplay,
  getUnionVisibleKpiConfigs,
  hasVisibleDisplayOnlyKpi,
  isDisplayOnlyKpi,
  KpiCellDisplay,
  KpiColumnConfig,
  KpiColumnTotals,
  setupCommReportDisplay,
  sortKpiConfigsByViewOrder
} from '../../shared/utils/commission-report-pivot.util';
import {
  CommReportApprovalStatusModalComponent
} from './comm-report-approval-status-modal.component';

@Component({
  selector: 'app-comm-report-config',
  templateUrl: './comm-report-config.component.html',
  styleUrls: ['./comm-report-config.component.css']
})
export class CommReportConfigComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('configScrollContainer') configScrollContainer?: ElementRef<HTMLElement>;
  @ViewChild('commissionReportTable') commissionReportTable?: ElementRef<HTMLTableElement>;

  public reviewCommissionsModel = new ReviewCommissionsModel();
  /** Single plan id used for configuration persistence. */
  configCommPlanId: number | null = null;

  searchDataList: ReviewCommissionsModel[] = [];
  salesChannelsList: ReviewCommissionsModel[] = [];
  commissionPlansList: ReviewCommissionsModel[] = [];
  payrollDatesList: ReviewCommissionsModel[] = [];

  pivotedData: CommReportEmployeeRow[] = [];
  filteredData: CommReportEmployeeRow[] = [];
  kpiColumnConfigs: KpiColumnConfig[] = [];
  configsByPlan: CommReportKpiConfigByPlan = {};
  visibleKpiConfigs: KpiColumnConfig[] = [];
  columnTotals: KpiColumnTotals[] = [];
  kpiHeaderSubtitles: string[] = [];

  filterText = '';
  sortColumn = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  savingConfig = false;
  showResults = false;
  showConfigPanel = true;
  grandTotalCommission = 0;
  filteredGrandTotal = 0;
  payrollMonth = '';

  employeeData: AddUser[] = [];
  filteredEmployeeValues: Observable<AddUser[]> | undefined;
  controlEmployee = new FormControl();
  selectedEmployeeName = '';
  selectedEmployeeId = '';
  approvalComments = '';

  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private filterDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly kpiCellLinesCache = new Map<string, { label: string; value: string; cssClass?: string }[]>();
  private configScrollWheelHandler: ((event: WheelEvent) => void) | null = null;
  private dragAutoScrollFrame: number | null = null;
  private frozenColumnSyncFrame: number | null = null;
  private readonly frozenColumnWindowResizeHandler = () => this.scheduleFrozenColumnSync();

  private readonly sessionScKey = 'SESSION_COMM_REPORT_SC';
  private readonly sessionCommPlanKey = 'SESSION_COMM_REPORT_COMMPLAN';
  private readonly sessionCalRunKey = 'SESSION_COMM_REPORT_CALRUNID';
  private readonly legacySessionKeys = [
    'SESSION_CONFIG_SC', 'SESSION_CONFIG_COMMPLAN', 'SESSION_CONFIG_CALRUNID',
    'SESSION_VIEW_SC', 'SESSION_VIEW_COMMPLAN', 'SESSION_VIEW_CALRUNID',
    'SESSION_NEW_SC', 'SESSION_NEW_COMMPLAN', 'SESSION_NEW_CALRUNID'
  ];

  constructor(
    private reviewCommissionsService: ReviewCommissionsService,
    private commissionReportConfigService: CommissionReportConfigService,
    private notificationService: NotificationService,
    private globalService: GlobalService,
    private exportExcelService: ExportExcelService,
    private manageCodesService: ManageCodesService,
    private dialog: MatDialog
  ) {
    globalService._disableBackButton();
  }

  ngOnInit(): void {
    this.getSearchData();
    this.filterEmployees();
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.frozenColumnWindowResizeHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.frozenColumnWindowResizeHandler);
    if (this.frozenColumnSyncFrame != null) {
      cancelAnimationFrame(this.frozenColumnSyncFrame);
    }
    this.detachConfigScrollWheelHandler();
    if (this.dragAutoScrollFrame != null) {
      cancelAnimationFrame(this.dragAutoScrollFrame);
    }
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
    }
  }

  toggleConfigPanel(): void {
    this.showConfigPanel = !this.showConfigPanel;
    this.scheduleFrozenColumnSync();
  }

  get includedKpiCount(): number {
    return this.kpiColumnConfigs.filter(c => c.includeKpi).length;
  }

  readonly chargebackNotice = CHARGEBACK_DISPLAY_NOTICE;
  readonly chargebackNoticeTitle = CHARGEBACK_DISPLAY_NOTICE_TITLE;

  get showChargebackNotice(): boolean {
    return hasVisibleDisplayOnlyKpi(this.visibleKpiConfigs);
  }

  isDisplayOnlyKpi = isDisplayOnlyKpi;

  getSearchData(): void {
    this.reviewCommissionsService.getSearchData()
      .subscribe(
        (response: ReviewCommissionsModel[]) => {
          this.searchDataList = response;
          this.reviewCommissionsService.setSearchDropdownCache(response);
          this.getSalesChannels();
        },
        (errorResponse: HttpErrorResponse) => {
          const msg: any = errorResponse;
          this.notificationService.sendNotification(NotificationType.ERROR, msg);
        }
      );
  }

  getSalesChannels(): void {
    this.salesChannelsList = this.searchDataList.filter((v, i) =>
      this.searchDataList.findIndex(item => item.salesChannel !== '' && item.salesChannel === v.salesChannel) === i
    );
    this.salesChannelsList.sort((a, b) => (a.salesChannel < b.salesChannel ? -1 : 1));
    this.loadDefaults();
  }

  loadDefaults(): void {
    const salesChannel = this.readStoredSessionValue(
      this.sessionScKey,
      ['SESSION_CONFIG_SC', 'SESSION_VIEW_SC', 'SESSION_NEW_SC']
    );
    if (!salesChannel) {
      return;
    }

    this.reviewCommissionsModel.salesChannel = salesChannel;
    const storedPlan = this.readStoredSessionValue(
      this.sessionCommPlanKey,
      ['SESSION_CONFIG_COMMPLAN', 'SESSION_VIEW_COMMPLAN', 'SESSION_NEW_COMMPLAN']
    );
    if (storedPlan) {
      const planId = Number(String(storedPlan).split(',')[0]);
      if (!Number.isNaN(planId)) {
        this.configCommPlanId = planId;
        this.reviewCommissionsModel.commissionPlanId = [planId];
      }
    }
    this.reviewCommissionsModel.calRunId = this.readStoredSessionValue(
      this.sessionCalRunKey,
      ['SESSION_CONFIG_CALRUNID', 'SESSION_VIEW_CALRUNID', 'SESSION_NEW_CALRUNID']
    );
    this.getCommissionPlans(this.reviewCommissionsModel.salesChannel);
  }

  getCommissionPlans(filterValue: string): void {
    this.commissionPlansList = this.searchDataList.filter(item => item.salesChannel === filterValue);
    this.commissionPlansList = this.commissionPlansList.filter((v, i) =>
      this.commissionPlansList.findIndex(item => item.commPlan !== '' && item.commPlan === v.commPlan) === i
    );
    this.commissionPlansList.sort((a, b) => (a.commPlan < b.commPlan ? -1 : 1));
    this.getPayrollDates(filterValue);
  }

  getPayrollDates(salesChannel: string): void {
    this.payrollDatesList = this.searchDataList.filter(item => item.salesChannel === salesChannel && item.payDate != null);
    this.payrollDatesList = this.payrollDatesList.filter((v, i) =>
      this.payrollDatesList.findIndex(item => item.calRunId === v.calRunId) === i
    );

    const now = new Date();
    const currentYearMonth = now.getFullYear() * 100 + (now.getMonth() + 1);
    this.payrollDatesList = this.payrollDatesList.filter(v => {
      const calRunId = Number(v.calRunId);
      return isNaN(calRunId) || calRunId <= currentYearMonth;
    });
    this.payrollDatesList = this.payrollDatesList.sort((a, b) => Number(b.calRunId) - Number(a.calRunId));
    this.syncPayrollDatesFromSelection();
  }

  private syncPayrollDatesFromSelection(): void {
    if (!this.reviewCommissionsModel.calRunId || this.configCommPlanId == null) {
      return;
    }

    const calRunKey = this.normalizeValue(String(this.reviewCommissionsModel.calRunId));
    const matched = this.payrollDatesList.find(item =>
      this.normalizeValue(String(item.calRunId)) === calRunKey
      && Number(item.commPlanId) === Number(this.configCommPlanId)
    );

    if (matched) {
      this.storePayrollCalendarDates(matched);
      return;
    }

    const resolved = this.reviewCommissionsService.resolvePayrollCalendarDates(
      this.reviewCommissionsModel.calRunId,
      this.configCommPlanId
    );

    if (resolved.validFromDt && resolved.validToDt) {
      this.storePayrollCalendarDates({
        validFromDt: resolved.validFromDt,
        validToDt: resolved.validToDt
      } as ReviewCommissionsModel);
    }
  }

  onCommissionPlanChange(): void {
    this.configCommPlanId = this.configCommPlanId != null ? Number(this.configCommPlanId) : null;
    this.reviewCommissionsModel.commissionPlanId = this.configCommPlanId != null ? [this.configCommPlanId] : [];
  }

  getSelectedSalesChannel(event: Event): void {
    const filterValue = (event.target as HTMLSelectElement).value;
    this.getCommissionPlans(filterValue);
  }

  getSelectedPayrollDate(event: Event): void {
    const filterValue = this.normalizeValue((event.target as HTMLSelectElement).value);
    const matched = this.payrollDatesList.filter(item => this.normalizeValue(item.calRunId) === filterValue);

    if (matched.length > 0) {
      this.storePayrollCalendarDates(matched[0]);
    }
  }

  search(): void {
    if (this.reviewCommissionsModel.salesChannel === 'null' || this.reviewCommissionsModel.salesChannel === null) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please select Sales Channel');
      return;
    }
    if (this.configCommPlanId == null || Number.isNaN(this.configCommPlanId)) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please select Commission Plan');
      return;
    }
    if (this.reviewCommissionsModel.calRunId === 'null' || this.reviewCommissionsModel.calRunId === null) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please select Payroll Date');
      return;
    }

    this.reviewCommissionsModel.commissionPlanId = [this.configCommPlanId];

    localStorage.setItem(this.sessionScKey, this.reviewCommissionsModel.salesChannel);
    localStorage.setItem(this.sessionCommPlanKey, String(this.configCommPlanId));
    localStorage.setItem(this.sessionCalRunKey, this.reviewCommissionsModel.calRunId);

    if (!this.applyPayrollCalendarDatesToModel()) {
      return;
    }

    this.loadCommReportData();
  }

  loadCommReportData(): void {
    this.showResults = false;
    const planId = this.configCommPlanId!;

    const savedConfigs$ = this.commissionReportConfigService.loadKpiConfigs([planId]).pipe(
      catchError(() => of({} as CommReportKpiConfigByPlan))
    );

    forkJoin({
      savedConfigs: savedConfigs$,
      reportData: this.reviewCommissionsService.getCommReportData(this.reviewCommissionsModel)
    }).subscribe(
      ({ savedConfigs, reportData }) => {
        this.applyDisplayState(setupCommReportDisplay(reportData, [planId], savedConfigs));
        this.setKpiColumnConfigsFromPlan(planId);
        this.showResults = true;
        this.showConfigPanel = true;
        this.scheduleFrozenColumnSync();
      },
      (errorResponse: HttpErrorResponse) => {
        const msg: any = errorResponse;
        this.notificationService.sendNotification(NotificationType.ERROR, msg);
      }
    );
  }

  onKpiConfigChange(config: KpiColumnConfig): void {
    ensureKpiConfigValid(config);
    this.syncConfigToPlan();
  }

  onKpiColumnDrop(event: CdkDragDrop<KpiColumnConfig[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const configs = [...this.kpiColumnConfigs];
    moveItemInArray(configs, event.previousIndex, event.currentIndex);
    this.kpiColumnConfigs = applyKpiConfigOrderFromList(configs);

    if (this.configCommPlanId != null) {
      this.configsByPlan[this.configCommPlanId] = this.kpiColumnConfigs.map(c => ({ ...c }));
      this.refreshVisibleColumns();
      this.scheduleSaveConfig();
    }
  }

  onKpiColumnDragStarted(): void {
    this.attachConfigScrollWheelHandler();
  }

  onKpiColumnDragMoved(event: CdkDragMove): void {
    const container = this.configScrollContainer?.nativeElement;
    if (!container) {
      return;
    }

    if (this.dragAutoScrollFrame != null) {
      cancelAnimationFrame(this.dragAutoScrollFrame);
    }

    this.dragAutoScrollFrame = requestAnimationFrame(() => {
      this.dragAutoScrollFrame = null;
      const rect = container.getBoundingClientRect();
      const y = event.pointerPosition.y;
      const edge = 48;
      const step = 14;

      if (y < rect.top + edge) {
        container.scrollTop = Math.max(0, container.scrollTop - step);
      } else if (y > rect.bottom - edge) {
        const maxScroll = container.scrollHeight - container.clientHeight;
        container.scrollTop = Math.min(maxScroll, container.scrollTop + step);
      }
    });
  }

  onKpiColumnDragEnded(): void {
    this.detachConfigScrollWheelHandler();
    if (this.dragAutoScrollFrame != null) {
      cancelAnimationFrame(this.dragAutoScrollFrame);
      this.dragAutoScrollFrame = null;
    }
  }

  trackKpiConfig(_index: number, config: KpiColumnConfig): string {
    return config.kpiName;
  }

  trackEmployeeRow(_index: number, emp: CommReportEmployeeRow): string {
    return emp.sc_emp_id;
  }

  readonly configMetricColumns: {
    field: keyof Pick<KpiColumnConfig, 'showUnits' | 'showGoal' | 'showPercentage' | 'showTier' | 'showRevenue' | 'showCommission'>;
    label: string;
    title: string;
    icon: string;
  }[] = [
    { field: 'showUnits', label: 'Units', title: 'Units sold', icon: 'fa-hashtag' },
    { field: 'showGoal', label: 'Goal', title: 'Goal target', icon: 'fa-bullseye' },
    { field: 'showPercentage', label: 'Perf', title: 'Performance to goal', icon: 'fa-percent' },
    { field: 'showTier', label: 'Tier', title: 'Tier level', icon: 'fa-layer-group' },
    { field: 'showRevenue', label: 'Revenue', title: 'Revenue amount', icon: 'fa-chart-line' },
    { field: 'showCommission', label: 'Comm', title: 'Commission dollars', icon: 'fa-dollar-sign' }
  ];

  getColumnBulkChecked(field: keyof KpiColumnConfig): boolean {
    const state = this.getColumnBulkState(field);
    return state === true;
  }

  getColumnBulkIndeterminate(field: keyof KpiColumnConfig): boolean {
    return this.getColumnBulkState(field) === 'indeterminate';
  }

  getColumnBulkState(field: keyof KpiColumnConfig): boolean | 'indeterminate' {
    const configs = field === 'includeKpi'
      ? this.kpiColumnConfigs
      : this.kpiColumnConfigs.filter(c => c.includeKpi);
    if (!configs.length) {
      return false;
    }
    const onCount = configs.filter(c => !!c[field]).length;
    if (onCount === 0) {
      return false;
    }
    if (onCount === configs.length) {
      return true;
    }
    return 'indeterminate';
  }

  onColumnBulkToggle(field: keyof KpiColumnConfig): void {
    const turnOn = this.getColumnBulkState(field) !== true;
    if (field === 'includeKpi') {
      this.selectAllKpis(turnOn);
      return;
    }
    this.setColumnForAll(field as 'showUnits' | 'showGoal' | 'showPercentage' | 'showTier' | 'showRevenue' | 'showCommission', turnOn);
  }

  applyConfigPreset(preset: 'performance' | 'units' | 'full'): void {
    this.kpiColumnConfigs.forEach(config => {
      config.includeKpi = true;
      switch (preset) {
        case 'performance':
          config.showUnits = true;
          config.showGoal = true;
          config.showPercentage = true;
          config.showCommission = true;
          config.showRevenue = false;
          config.showTier = false;
          break;
        case 'units':
          config.showUnits = true;
          config.showGoal = false;
          config.showPercentage = false;
          config.showCommission = true;
          config.showRevenue = false;
          config.showTier = false;
          break;
        case 'full':
          config.showUnits = true;
          config.showGoal = true;
          config.showPercentage = true;
          config.showCommission = true;
          config.showRevenue = true;
          config.showTier = true;
          break;
      }
      ensureKpiConfigValid(config);
    });
    this.syncConfigToPlan();
  }

  private setColumnForAll(
    field: 'showUnits' | 'showGoal' | 'showPercentage' | 'showTier' | 'showRevenue' | 'showCommission',
    show: boolean
  ): void {
    this.kpiColumnConfigs.forEach(config => {
      if (config.includeKpi) {
        config[field] = show;
        ensureKpiConfigValid(config);
      }
    });
    this.syncConfigToPlan();
  }

  selectAllKpis(include: boolean): void {
    this.kpiColumnConfigs.forEach(config => {
      config.includeKpi = include;
      ensureKpiConfigValid(config);
    });
    this.syncConfigToPlan();
  }

  setAllUnits(show: boolean): void {
    this.kpiColumnConfigs.forEach(config => {
      if (config.includeKpi) {
        config.showUnits = show;
        ensureKpiConfigValid(config);
      }
    });
    this.syncConfigToPlan();
  }

  setAllCommission(show: boolean): void {
    this.kpiColumnConfigs.forEach(config => {
      if (config.includeKpi) {
        config.showCommission = show;
        ensureKpiConfigValid(config);
      }
    });
    this.syncConfigToPlan();
  }

  setAllGoals(show: boolean): void {
    this.kpiColumnConfigs.forEach(config => {
      if (config.includeKpi) {
        config.showGoal = show;
        ensureKpiConfigValid(config);
      }
    });
    this.syncConfigToPlan();
  }

  setAllPercentages(show: boolean): void {
    this.kpiColumnConfigs.forEach(config => {
      if (config.includeKpi) {
        config.showPercentage = show;
        ensureKpiConfigValid(config);
      }
    });
    this.syncConfigToPlan();
  }

  setAllRevenue(show: boolean): void {
    this.kpiColumnConfigs.forEach(config => {
      if (config.includeKpi) {
        config.showRevenue = show;
        ensureKpiConfigValid(config);
      }
    });
    this.syncConfigToPlan();
  }

  setAllTiers(show: boolean): void {
    this.kpiColumnConfigs.forEach(config => {
      if (config.includeKpi) {
        config.showTier = show;
        ensureKpiConfigValid(config);
      }
    });
    this.syncConfigToPlan();
  }

  getKpiCellLinesCached(emp: CommReportEmployeeRow, config: KpiColumnConfig) {
    return this.kpiCellLinesCache.get(`${emp.sc_emp_id}|${config.kpiName}`) || [];
  }

  onFilterInput(): void {
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
    }

    this.filterDebounceTimer = setTimeout(() => {
      this.filterDebounceTimer = null;
      this.applyFilter();
    }, 250);
  }

  resetToDefaults(): void {
    this.kpiColumnConfigs = sortKpiConfigsByViewOrder(buildAllOnKpiConfigs(
      this.kpiColumnConfigs.map(c => c.kpiName),
      new Map(this.kpiColumnConfigs.map(c => [c.kpiName, c.viewOrder]))
    ));
    this.syncConfigToPlan();
    this.scheduleSaveConfig(true);
  }

  exportToExcel(): void {
    if (!this.pivotedData.length) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'No data available to export');
      return;
    }
    if (!this.visibleKpiConfigs.length) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Select at least one KPI column to export');
      return;
    }

    this.exportExcelService.exportExcel(
      buildCommissionReportExcelPayload(
        this.pivotedData,
        this.visibleKpiConfigs,
        this.payrollMonth,
        this.grandTotalCommission,
        this.configsByPlan
      )
    );
    this.notificationService.sendNotification(NotificationType.SUCCESS, 'Report exported successfully');
  }

  openApprovalStatus(): void {
    this.dialog.open(CommReportApprovalStatusModalComponent, {
      width: '960px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      data: {
        salesChannel: this.reviewCommissionsModel.salesChannel,
        commPlanId: this.configCommPlanId,
        calRunId: this.reviewCommissionsModel.calRunId,
        payrollMonth: this.payrollMonth
      }
    });
  }

  sendForApproval(): void {
    if (!this.selectedEmployeeId || !this.selectedEmployeeName) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please select an employee in Send To field');
      return;
    }

    if (!this.pivotedData.length) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'No report data available to send');
      return;
    }

    if (!this.visibleKpiConfigs.length) {
      this.notificationService.sendNotification(NotificationType.ERROR,
        'Select at least one KPI column before sending for approval');
      return;
    }

    const approvalModel = new ReviewCommissionsModel();
    approvalModel.salesChannel = this.reviewCommissionsModel.salesChannel;
    approvalModel.commissionPlanId = this.reviewCommissionsModel.commissionPlanId;
    approvalModel.calRunId = this.reviewCommissionsModel.calRunId;
    approvalModel.effectiveDateFrom = this.formatEffectiveDateForSend();
    approvalModel.payrollMonth = this.getPayrollMonthYy();
    approvalModel.sendToEmployeeId = this.selectedEmployeeId;
    approvalModel.createdBy = localStorage.getItem('CURRENT_EMPLOYEEID_SESSION')
      || localStorage.getItem('CURRENT_USER_NAME_SESSION')
      || '';
    approvalModel.loggedinRole = localStorage.getItem('CURRENT_USERROLE_SESSION')
      || localStorage.getItem('SESSION_ROLE')
      || 'ADMIN';
    approvalModel.message = this.approvalComments || '';
    approvalModel.totalCommission = this.grandTotalCommission;
    // Freeze what the admin configured/sent so supervisors do not load live KPI settings
    approvalModel.reportSnapshot = {
      version: 1,
      payrollMonth: this.payrollMonth,
      grandTotalCommission: this.grandTotalCommission,
      visibleKpiConfigs: this.visibleKpiConfigs,
      configsByPlan: this.configsByPlan,
      pivotedData: this.pivotedData
    };

    if (!approvalModel.payrollMonth) {
      this.notificationService.sendNotification(NotificationType.ERROR,
        'Could not determine payroll month. Please re-select Payroll Date, run Search, and try again.');
      return;
    }

    this.reviewCommissionsService.sendForApproval(approvalModel)
      .subscribe(
        (response: any) => {
          if (response.success) {
            this.notificationService.sendNotification(NotificationType.SUCCESS,
              `Report sent to ${this.selectedEmployeeName} for approval. Tracking ID: ${response.trackingId}`);
            this.selectedEmployeeId = '';
            this.selectedEmployeeName = '';
            this.approvalComments = '';
            this.controlEmployee.setValue('');
          } else {
            this.notificationService.sendNotification(NotificationType.ERROR,
              response.message || 'Failed to send report for approval');
          }
        },
        (errorResponse: HttpErrorResponse) => {
          this.notificationService.sendNotification(NotificationType.ERROR,
            'Error sending report for approval: ' + (errorResponse.error?.message || errorResponse.message));
        }
      );
  }

  filterEmployees(): void {
    this.filteredEmployeeValues = this.controlEmployee.valueChanges.pipe(
      startWith(''),
      map(value => this.filterEmployee(value))
    );
  }

  getEmployeeId(event: any): void {
    this.selectedEmployeeName = event.option.value;
    const lastIndex = this.selectedEmployeeName.lastIndexOf('-');
    if (lastIndex > 0) {
      this.selectedEmployeeId = this.selectedEmployeeName.substring(lastIndex + 1);
    }
  }

  clearSearchCriteria(): void {
    localStorage.removeItem(this.sessionScKey);
    localStorage.removeItem(this.sessionCommPlanKey);
    localStorage.removeItem(this.sessionCalRunKey);
    this.legacySessionKeys.forEach(key => localStorage.removeItem(key));

    this.selectedEmployeeId = '';
    this.selectedEmployeeName = '';
    this.approvalComments = '';
    this.controlEmployee.setValue('');

    this.reviewCommissionsModel.salesChannel = null;
    this.reviewCommissionsModel.commissionPlanId = [];
    this.configCommPlanId = null;
    this.reviewCommissionsModel.calRunId = null;
    this.commissionPlansList = [];
    this.payrollDatesList = [];
    this.resetDisplayState();
    this.getSearchData();
  }

  applyFilter(): void {
    const filterValue = this.filterText.toLowerCase().trim();

    if (!filterValue) {
      this.filteredData = [...this.pivotedData];
    } else {
      this.filteredData = this.pivotedData.filter(emp =>
        emp.empname?.toLowerCase().includes(filterValue) ||
        emp.sales_rep_id?.toLowerCase().includes(filterValue) ||
        emp.employee_id?.toLowerCase().includes(filterValue) ||
        emp.supervisor_name?.toLowerCase().includes(filterValue) ||
        emp.manager_name?.toLowerCase().includes(filterValue) ||
        emp.comm_plan?.toLowerCase().includes(filterValue) ||
        this.visibleKpiConfigs.some(config => {
          const kpiData = emp.kpis[config.kpiName];
          return kpiData && (
            config.kpiName.toLowerCase().includes(filterValue) ||
            kpiData.commission_val?.toString().includes(filterValue) ||
            kpiData.units?.toString().includes(filterValue) ||
            kpiData.goal?.toString().includes(filterValue) ||
            kpiData.percentage?.toString().includes(filterValue) ||
            kpiData.revenue?.toString().includes(filterValue)
          );
        })
      );
    }

    if (this.sortColumn) {
      this.applySorting();
    }

    this.filteredGrandTotal = this.filteredData.reduce((sum, emp) => sum + emp.total_commission, 0);
    this.rebuildTableDisplayCache();
  }

  clearFilter(): void {
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = null;
    }
    this.filterText = '';
    this.applyFilter();
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.applySorting();
    this.filteredGrandTotal = this.filteredData.reduce((sum, emp) => sum + emp.total_commission, 0);
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) {
      return 'fas fa-sort';
    }
    return this.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
  }

  getCellDisplay(emp: CommReportEmployeeRow, config: KpiColumnConfig): KpiCellDisplay {
    return getKpiCellDisplay(this.configsByPlan, emp.comm_plan_id, config.kpiName, config);
  }

  private applyDisplayState(state: CommReportDisplayState): void {
    this.pivotedData = state.pivotedData;
    this.filteredData = state.filteredData;
    this.configsByPlan = state.configsByPlan;
    this.visibleKpiConfigs = state.visibleKpiConfigs;
    this.payrollMonth = state.payrollMonth;
    this.grandTotalCommission = state.grandTotalCommission;
    this.filteredGrandTotal = state.filteredGrandTotal;
    this.rebuildTableDisplayCache();
    this.scheduleFrozenColumnSync();
  }

  private resetDisplayState(): void {
    this.pivotedData = [];
    this.filteredData = [];
    this.kpiColumnConfigs = [];
    this.configsByPlan = {};
    this.visibleKpiConfigs = [];
    this.columnTotals = [];
    this.kpiHeaderSubtitles = [];
    this.kpiCellLinesCache.clear();
    this.showResults = false;
    this.grandTotalCommission = 0;
    this.filteredGrandTotal = 0;
    this.payrollMonth = '';
  }

  private rebuildTableDisplayCache(): void {
    this.columnTotals = calculateKpiColumnTotals(
      this.filteredData,
      this.visibleKpiConfigs,
      this.configsByPlan
    );
    this.kpiHeaderSubtitles = this.visibleKpiConfigs.map(config =>
      getKpiColumnSubtitleParts(config).join(' · ')
    );

    this.kpiCellLinesCache.clear();
    for (const emp of this.filteredData) {
      for (const config of this.visibleKpiConfigs) {
        const cell = getKpiCellDisplay(this.configsByPlan, emp.comm_plan_id, config.kpiName, config);
        this.kpiCellLinesCache.set(
          `${emp.sc_emp_id}|${config.kpiName}`,
          getKpiCellDisplayLines(emp.kpis[config.kpiName], cell, config.kpiName)
        );
      }
    }
  }

  private setKpiColumnConfigsFromPlan(planId: number): void {
    let configs = sortKpiConfigsByViewOrder([...(this.configsByPlan[planId] || [])]);
    const distinctOrders = new Set(configs.map(c => c.viewOrder));

    if (distinctOrders.size < configs.length) {
      configs = applyKpiConfigOrderFromList(configs);
      this.configsByPlan[planId] = configs.map(c => ({ ...c }));
    }

    this.kpiColumnConfigs = configs;
  }

  private attachConfigScrollWheelHandler(): void {
    this.detachConfigScrollWheelHandler();

    this.configScrollWheelHandler = (event: WheelEvent) => {
      const container = this.configScrollContainer?.nativeElement;
      if (!container) {
        return;
      }

      const rect = container.getBoundingClientRect();
      if (event.clientY < rect.top || event.clientY > rect.bottom) {
        return;
      }

      container.scrollTop += event.deltaY;
      event.preventDefault();
    };

    document.addEventListener('wheel', this.configScrollWheelHandler, { passive: false });
  }

  private detachConfigScrollWheelHandler(): void {
    if (!this.configScrollWheelHandler) {
      return;
    }

    document.removeEventListener('wheel', this.configScrollWheelHandler);
    this.configScrollWheelHandler = null;
  }

  private syncConfigToPlan(): void {
    if (this.configCommPlanId == null) {
      return;
    }
    this.configsByPlan[this.configCommPlanId] = sortKpiConfigsByViewOrder(
      this.kpiColumnConfigs.map(c => ({ ...c }))
    );
    this.kpiColumnConfigs = [...this.configsByPlan[this.configCommPlanId]];
    this.refreshVisibleColumns();
    this.scheduleSaveConfig();
  }

  private refreshVisibleColumns(): void {
    if (this.configCommPlanId == null) {
      return;
    }
    this.visibleKpiConfigs = getUnionVisibleKpiConfigs(this.configsByPlan, [this.configCommPlanId]);
    this.grandTotalCommission = applyConfiguredTotals(this.pivotedData, this.configsByPlan, this.visibleKpiConfigs);
    this.applyFilter();
    this.scheduleFrozenColumnSync();
  }

  private scheduleSaveConfig(immediate = false): void {
    if (this.configCommPlanId == null) {
      return;
    }

    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }

    const delay = immediate ? 0 : 600;
    this.saveTimer = setTimeout(() => this.saveConfigToServer(), delay);
  }

  private saveConfigToServer(): void {
    if (this.configCommPlanId == null || !this.kpiColumnConfigs.length) {
      return;
    }

    this.savingConfig = true;
    const updatedBy = localStorage.getItem('CURRENT_EMPLOYEEID_SESSION')
      || localStorage.getItem('CURRENT_USER_NAME_SESSION')
      || 'ADMIN';

    this.commissionReportConfigService.saveKpiConfig(
      this.configCommPlanId,
      this.reviewCommissionsModel.salesChannel,
      this.kpiColumnConfigs,
      updatedBy
    ).subscribe(
      response => {
        this.savingConfig = false;
        if (!response?.success) {
          this.notificationService.sendNotification(NotificationType.ERROR,
            response?.message || 'Failed to save configuration');
        }
      },
      (error: HttpErrorResponse | string) => {
        this.savingConfig = false;
        const message = typeof error === 'string'
          ? error
          : (error?.error?.message || error?.message || 'Failed to save configuration');
        this.notificationService.sendNotification(NotificationType.ERROR, message);
      }
    );
  }

  private applySorting(): void {
    const visibleNames = this.visibleKpiConfigs.map(c => c.kpiName);

    this.filteredData.sort((a, b) => {
      let valueA: any;
      let valueB: any;

      if (visibleNames.includes(this.sortColumn)) {
        valueA = a.kpis[this.sortColumn]?.commission_val || 0;
        valueB = b.kpis[this.sortColumn]?.commission_val || 0;
      } else if (this.sortColumn === 'total_commission') {
        valueA = a.total_commission || 0;
        valueB = b.total_commission || 0;
      } else {
        valueA = (a as any)[this.sortColumn] || '';
        valueB = (b as any)[this.sortColumn] || '';
      }

      if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase();
        valueB = String(valueB).toLowerCase();
      }

      if (valueA < valueB) {
        return this.sortDirection === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  private normalizeValue(value: string): string {
    return value ? value.toString().toLowerCase().replace(/\s/g, '') : '';
  }

  /** Match Review Commissions search: store validFromDt in validToDt for effective-date SQL params. */
  private storePayrollCalendarDates(payrollOption: ReviewCommissionsModel): void {
    localStorage.setItem('validFromDt', ReviewCommissionsService.toStoredDateValue(payrollOption.validFromDt));
    localStorage.setItem('validToDt', ReviewCommissionsService.toStoredDateValue(payrollOption.validFromDt));
    localStorage.setItem('validLevelToDt', ReviewCommissionsService.toStoredDateValue(payrollOption.validToDt));
  }

  /**
   * Align effective date with Review Commissions ListUsers.
   * Backend getCommReportData reads model.validToDt as param_effectivedate.
   */
  private applyPayrollCalendarDatesToModel(): boolean {
    const resolved = this.reviewCommissionsService.resolvePayrollCalendarDates(
      this.reviewCommissionsModel.calRunId,
      this.configCommPlanId
    );

    if (resolved.validFromDt && resolved.validToDt) {
      this.storePayrollCalendarDates({
        validFromDt: resolved.validFromDt,
        validToDt: resolved.validToDt
      } as ReviewCommissionsModel);
    }

    const effectiveDate = ReviewCommissionsService.toDate(localStorage.getItem('validToDt'))
      || resolved.validFromDt
      || ReviewCommissionsService.toDate(localStorage.getItem('validFromDt'));

    const periodEndDate = ReviewCommissionsService.toDate(localStorage.getItem('validLevelToDt'))
      || resolved.validToDt;

    if (!effectiveDate) {
      this.notificationService.sendNotification(
        NotificationType.ERROR,
        'Could not resolve payroll effective date. Please re-select Payroll Date and try again.'
      );
      return false;
    }

    this.reviewCommissionsModel.validFromDt = effectiveDate;
    this.reviewCommissionsModel.validToDt = effectiveDate;
    if (periodEndDate) {
      this.reviewCommissionsModel.payDate = periodEndDate;
    }

    return true;
  }

  private readStoredSessionValue(primaryKey: string, legacyKeys: string[]): string | null {
    const primary = localStorage.getItem(primaryKey);
    if (primary) {
      return primary;
    }
    for (const key of legacyKeys) {
      const value = localStorage.getItem(key);
      if (value) {
        return value;
      }
    }
    return null;
  }

  private filterEmployee(value: string): AddUser[] {
    const filterValue = (value || '').toLowerCase();

    if (value && value.length >= 3) {
      this.manageCodesService.getEmpSupervisors(value)
        .subscribe(
          (data: any) => {
            this.employeeData = data;
          },
          (errorResponse: HttpErrorResponse) => {
            const msg: any = errorResponse;
            this.notificationService.sendNotification(NotificationType.ERROR, msg);
          }
        );
    }

    return this.employeeData.filter(item =>
      item.supervisor_name && item.supervisor_name.toLowerCase().includes(filterValue)
    );
  }

  private parsePayrollDate(value: any): Date | null {
    if (value == null || value === '') {
      return null;
    }

    if (value instanceof Date && !isNaN(value.getTime())) {
      return value;
    }

    const str = String(value).trim();
    if (/^\d{13}$/.test(str)) {
      const epochDate = new Date(Number(str));
      return isNaN(epochDate.getTime()) ? null : epochDate;
    }

    let date = new Date(str);
    if (!isNaN(date.getTime())) {
      return date;
    }

    const parts = str.split(/[T\s]/)[0].split('-');
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      } else if (parts[2].length === 4) {
        date = new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1]));
      }
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    return null;
  }

  private formatEffectiveDateForSend(): string {
    const periodEnd = this.getPayrollPeriodEndDate();
    if (!periodEnd) {
      return '';
    }

    const year = periodEnd.getFullYear();
    const month = String(periodEnd.getMonth() + 1).padStart(2, '0');
    const day = String(periodEnd.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getPayrollMonthYy(): string {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];

    if (this.payrollMonth) {
      const parts = this.payrollMonth.split('/');
      if (parts.length === 2) {
        const monthIdx = monthNames.indexOf(parts[0]);
        if (monthIdx >= 0) {
          const year = parts[1].length === 4 ? parts[1].slice(-2) : parts[1];
          return `${String(monthIdx + 1).padStart(2, '0')}/${year}`;
        }
      }
    }

    const parsed = this.getPayrollPeriodEndDate();
    if (!parsed) {
      return '';
    }

    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = String(parsed.getFullYear()).slice(-2);
    return `${month}/${year}`;
  }

  private getPayrollPeriodEndDate(): Date | null {
    return ReviewCommissionsService.toDate(localStorage.getItem('validLevelToDt'))
      || this.reviewCommissionsModel.payDate
      || ReviewCommissionsService.toDate(localStorage.getItem('validFromDt'));
  }

  private scheduleFrozenColumnSync(): void {
    if (this.frozenColumnSyncFrame != null) {
      cancelAnimationFrame(this.frozenColumnSyncFrame);
    }

    this.frozenColumnSyncFrame = requestAnimationFrame(() => {
      this.frozenColumnSyncFrame = requestAnimationFrame(() => {
        this.frozenColumnSyncFrame = null;
        this.syncFrozenColumnOffsets();
      });
    });
  }

  private syncFrozenColumnOffsets(): void {
    const table = this.commissionReportTable?.nativeElement;
    if (!table || !this.showResults || !this.visibleKpiConfigs.length) {
      return;
    }

    const headerRow = table.tHead?.rows[0];
    if (!headerRow) {
      return;
    }

    const frozenHeaders = Array.from(headerRow.cells).filter(cell =>
      cell.classList.contains('frozen-col')
    ) as HTMLTableCellElement[];

    if (!frozenHeaders.length) {
      return;
    }

    let left = 0;
    const offsets: number[] = [];

    frozenHeaders.forEach(cell => {
      offsets.push(left);
      left += cell.getBoundingClientRect().width;
    });

    const applyOffset = (cell: HTMLElement, index: number): void => {
      if (index < offsets.length) {
        cell.style.left = `${offsets[index]}px`;
      }
    };

    frozenHeaders.forEach((cell, index) => applyOffset(cell, index));

    Array.from(table.tBodies[0]?.rows || []).forEach(row => {
      let frozenIndex = 0;
      Array.from(row.cells).forEach(cell => {
        if (cell.classList.contains('frozen-col')) {
          applyOffset(cell as HTMLElement, frozenIndex);
          frozenIndex++;
        }
      });
    });

    const footerLabel = table.querySelector('td.frozen-footer-label') as HTMLElement | null;
    if (footerLabel) {
      footerLabel.style.left = '0px';
      footerLabel.style.minWidth = `${left}px`;
    }
  }
}
